import { h, Fragment } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import PropTypes, { number } from 'prop-types';

import PlaylistCarouselInlineComponent from '../../components/4-organisms/playlist-carousel-inline/playlist-carousel-inline';
import VideoPlaylistPopupComponent from '../../components/4-organisms/video-playlist-popup/video-playlist-popup';
import { commonStore } from '../../../global-store/subjects/common';
import { getLocation } from '../../../utils/build-urls';
import { initDatalayerElement } from '../../../components/tracking/datalayer';
import { modifierList } from '../../components/4-organisms/playlist-carousel-inline/props-list';
import { ASPECT_RATIO_16_10, ASPECT_RATIO_16_9 } from '../../components/3-molecules/card/card';

import { getPlaylistInfo, getPlaylistData } from './api';

const PlaylistCarouselInline = ({
  htmlProps: { playlistID, headerTitle, title, readMore, advOff, modifiers, cardList, cardsStyles },
  widget: { cardsToSlide, cardTag, cardWidth },
  $root,
}) => {
  const [playlistVideos, setPlaylistVideos] = useState();
  const [playlistInfo, setPlaylistInfo] = useState('');
  const [showVideoPlaylistPopup, setShowVideoPlaylistPopup] = useState(false);
  const [indexOnPlay, setIndexOnPlay] = useState();
  const [focusedCardRef, setFocusedCardRef] = useState();

  const locationWithPathname = getLocation().withPathName;
  const mappedCardList = cardList?.filter((card) => card.url !== locationWithPathname);

  const fetchPlaylistData = async () => {
    const playlistData = await getPlaylistData(playlistID);
    const playlistVideos = playlistData?.map((video) => ({
      id: video.asset_id,
      imageSrc: video.video_still_medium,
      title: video.title,
      duration: video.duration,
      publishedTime: video.publication_date,
    }));
    setPlaylistVideos(playlistVideos);
  };
  const fetchPlaylistInfo = async () => {
    const playlistInfo = await getPlaylistInfo(playlistID);
    setPlaylistInfo(playlistInfo);
  };

  const handleCardClick = (index) => {
    setIndexOnPlay(index);
  };

  const handleToggleShowPopup = () => {
    if (!showVideoPlaylistPopup) {
      // When we open the VPI popup
      const playersOnPage = commonStore.getValue().playersOnPage;
      playersOnPage.forEach((player) => {
        player.pause();
      });
    }

    setShowVideoPlaylistPopup(!showVideoPlaylistPopup);

    if (showVideoPlaylistPopup && focusedCardRef) {
      // When we close the VPI popup
      focusedCardRef.focus();
    }
  };

  useEffect(() => {
    if (playlistID) {
      fetchPlaylistData();
      fetchPlaylistInfo();
    }
  }, []);

  useEffect(() => {
    if (cardList?.length || readMore) {
      initDatalayerElement($root);
    }
  }, [cardList?.length, readMore]);

  return (
    <Fragment>
      <PlaylistCarouselInlineComponent
        playlistVideos={playlistVideos}
        cardList={mappedCardList}
        title={title || playlistInfo.title}
        readMore={readMore}
        cardsToSlide={cardsToSlide}
        cardTag={cardTag}
        cardWidth={cardWidth}
        modifiers={modifiers}
        cardsStyles={cardsStyles}
        onCardClick={(index, e) => {
          handleCardClick(index);
          handleToggleShowPopup();
          setFocusedCardRef(e.target);
        }}
      />
      {playlistVideos?.length > 0 && (
        <VideoPlaylistPopupComponent
          isActive={showVideoPlaylistPopup}
          onCancel={handleToggleShowPopup}
          headerTitle={headerTitle}
          title={playlistInfo.title}
          sectionLabel={playlistInfo.subchannel}
          playlistVideos={playlistVideos}
          advOff={advOff}
          indexOnPlay={indexOnPlay}
          onCardClick={handleCardClick}
        />
      )}
    </Fragment>
  );
};

PlaylistCarouselInline.propTypes = {
  htmlProps: PropTypes.shape({
    playlistID: PropTypes.string,
    headerTitle: PropTypes.string,
    advOff: PropTypes.bool,
    title: PropTypes.string,
    readMore: PropTypes.shape({
      text: PropTypes.string,
      url: PropTypes.string,
      intcmp: PropTypes.string,
    }),
    modifiers: PropTypes.shape({
      mobile: PropTypes.arrayOf(PropTypes.oneOf(modifierList)),
      desktop: PropTypes.arrayOf(PropTypes.oneOf(modifierList)),
    }),
    cardList: PropTypes.arrayOf(
      PropTypes.shape({
        section: PropTypes.string,
        title: PropTypes.string,
        url: PropTypes.string,
        urlImage: PropTypes.shape({
          mobile: PropTypes.string,
          desktop: PropTypes.string,
        }),
        intcmp: PropTypes.string,
        target: PropTypes.string,
        time: PropTypes.string,
        rel: PropTypes.string,
        imageAspectRatio: PropTypes.shape({
          mobile: PropTypes.oneOf([ASPECT_RATIO_16_9, ASPECT_RATIO_16_10, '']),
          desktop: PropTypes.oneOf([ASPECT_RATIO_16_9, ASPECT_RATIO_16_10, '']),
        }),
        labelModifiers: PropTypes.array,
      })
    ),
  }),
  $root: PropTypes.object.isRequired,
  widget: PropTypes.shape({
    cardsToSlide: PropTypes.number.isRequired,
    cardTag: PropTypes.string,
    cardWidth: PropTypes.shape({
      [PropTypes.string]: PropTypes.shape({
        mobile: PropTypes.number,
        desktop: PropTypes.number,
      }),
    }).isRequired,
  }).isRequired,
};

export default PlaylistCarouselInline;
